import React, { useMemo, useState } from 'react';
import { TabView, TabPanel } from 'primereact/tabview';
import { Accordion, AccordionTab } from 'primereact/accordion';
import { Dropdown } from 'primereact/dropdown';
import ConceptDetailsSection from 'screens/FDP/components/ConceptDetails';
import NotesSection from 'screens/FDPScreen/components/NotesSection';
import NDCHeader from 'screens/FDPScreen/components/ConstraintForecastScreen/components/NDCHeader';
import classes from './constrainedView.module.scss';
import {
  getFutureMonths15,
  mockConceptDetails,
  mockContractDetails,
  mockNotesData,
  mockDirectNDCs,
  mockIndirectNDCs,
  mockForecastData,
  mockInvoiceSales,
  mockPlanningPeriods,
  PlanningPeriod,
} from '../constrainedMockData';

const formatNumber = (val: number): string => val.toLocaleString('en-US');

const ConstrainedForecastView: React.FC = () => {
  const forecastingMonths = useMemo(() => getFutureMonths15(), []);

  const [directCurrentNDCIndex, setDirectCurrentNDCIndex] = useState(0);
  const [indirectCurrentNDCIndex, setIndirectCurrentNDCIndex] = useState(0);
  const [selectedPeriod, setSelectedPeriod] = useState<PlanningPeriod>(mockPlanningPeriods[1]);

  // ---------------------------------------------------------------------------
  // Helpers
  // ---------------------------------------------------------------------------

  const getInvoiceSalesDetails = (ndcKey: string, metric: 'ytd' | '3ma' | 'pm'): string => {
    const entry = mockInvoiceSales.find((s) => s.NDC11 === ndcKey);
    if (!entry) return '-';
    if (metric === 'ytd') return formatNumber(entry.YTDAverage);
    if (metric === '3ma') return formatNumber(entry.ThreeMonthAverage);
    return formatNumber(entry.LastMonthAverage);
  };

  const getUnconstrainedForecast = (ndcKey: string, dateString: string): string => {
    const entry = mockForecastData.find((f) => f.NDC11 === ndcKey);
    const detail = entry?.forecast_details.find((d) => d.ForecastPeriod === dateString);
    return detail ? formatNumber(detail.UnconstrainedForecast) : '-';
  };

  const getConstrainedForecast = (ndcKey: string, dateString: string): string => {
    const entry = mockForecastData.find((f) => f.NDC11 === ndcKey);
    const detail = entry?.forecast_details.find((d) => d.ForecastPeriod === dateString);
    return detail ? formatNumber(detail.ConstrainedForecast) : '-';
  };

  // ---------------------------------------------------------------------------
  // Per-NDC content
  // ---------------------------------------------------------------------------

  const renderNDCContent = (ndcKey: string, packSize: string) => (
    <div className={classes.contentContainer}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          paddingLeft: '1rem',
          gap: '5rem',
          marginTop: '1rem',
          marginBottom: '1rem',
          justifyContent: 'center',
        }}
      >
        <div>
          <p>Pack Size: <span>{packSize}</span></p>
        </div>
        <div className={classes.saleDetails}>
          <div>
            <div style={{ fontSize: '14px', fontWeight: '600', paddingLeft: '3rem' }}>
              YTD: {getInvoiceSalesDetails(ndcKey, 'ytd')}
            </div>
            <div className={classes.vertical}></div>
            <div style={{ fontSize: '14px', fontWeight: '600' }}>
              3MA: {getInvoiceSalesDetails(ndcKey, '3ma')}
            </div>
            <div className={classes.vertical}></div>
            <div style={{ fontSize: '14px', fontWeight: '600', paddingRight: '3rem' }}>
              PM: {getInvoiceSalesDetails(ndcKey, 'pm')}
            </div>
          </div>
        </div>
      </div>

      <div className={classes.forecastTable}>
        <table>
          <thead>
            <tr>
              <th style={{ fontSize: '15px' }}>Forecast Period</th>
              <th style={{ fontSize: '15px' }}>Unconstrained Forecast</th>
              <th style={{ fontSize: '15px' }}>Constrained Forecast</th>
            </tr>
          </thead>
          <tbody>
            {forecastingMonths.map((month) => (
              <tr
                key={month.dateString}
                className={month.isExtended ? classes.extendedMonth : undefined}
              >
                <td>{month.label}</td>
                <td>{getUnconstrainedForecast(ndcKey, month.dateString)}</td>
                <td>{getConstrainedForecast(ndcKey, month.dateString)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  // ---------------------------------------------------------------------------
  // Main render
  // ---------------------------------------------------------------------------

  return (
    <div className={classes.viewContainer}>
      {/* Concept Details */}
      <div className={classes.conceptContainer}>
        <div className={classes.heading}>
          <h2>Constrained Forecast</h2>
        </div>
        <div>
          <ConceptDetailsSection
            conceptDetails={mockConceptDetails}
            contractDetails={mockContractDetails}
            mode="view"
            path={`edit?gfc=${mockConceptDetails.gfc}`}
            label="New Forecast"
            loading={false}
            isDisabled={false}
          />
        </div>
      </div>

      {/* Main content row: forecastCard + NotesSection */}
      <div style={{ display: 'flex', gap: '1rem', padding: '.5rem' }}>
        <div className={classes.forecastCard}>
          {/* Choose Plan */}
          <div className={classes.plan}>
            <div>
              <span>Choose Plan: </span>
            </div>
            <div>
              <Dropdown
                value={selectedPeriod}
                options={mockPlanningPeriods}
                optionLabel="label"
                onChange={(e) => setSelectedPeriod(e.value)}
                style={{ width: '15rem' }}
              />
            </div>
          </div>

          <div>
            <div style={{ display: 'flex', flexDirection: 'column', margin: '1rem' }}>
              {/* Direct Accordion */}
              {mockDirectNDCs.length > 0 && (
                <Accordion activeIndex={0}>
                  <AccordionTab header="Direct">
                    <div className={classes.directtabContainer}>
                      <TabView
                        scrollable
                        tabIndex={directCurrentNDCIndex}
                        onTabChange={(e) => setDirectCurrentNDCIndex(e.index)}
                      >
                        {mockDirectNDCs.map((ndc, index) => (
                          <TabPanel
                            key={ndc.ndc}
                            header={
                              <NDCHeader
                                heading={ndc.ndc}
                                isSelected={directCurrentNDCIndex === index}
                                tabIndex={index}
                              />
                            }
                          >
                            {renderNDCContent(ndc.ndc, ndc.packSize)}
                          </TabPanel>
                        ))}
                      </TabView>
                    </div>
                  </AccordionTab>
                </Accordion>
              )}

              {/* Indirect Accordion */}
              {mockIndirectNDCs.length > 0 && (
                <Accordion activeIndex={mockDirectNDCs.length === 0 ? 0 : null}>
                  <AccordionTab header="Indirect">
                    <div className={classes.directtabContainer}>
                      <TabView
                        scrollable
                        tabIndex={indirectCurrentNDCIndex}
                        onTabChange={(e) => setIndirectCurrentNDCIndex(e.index)}
                      >
                        {mockIndirectNDCs.map((ndc, index) => (
                          <TabPanel
                            key={ndc.ndc}
                            header={
                              <NDCHeader
                                heading={ndc.ndc}
                                isSelected={indirectCurrentNDCIndex === index}
                                tabIndex={index}
                              />
                            }
                          >
                            {renderNDCContent(ndc.ndc, ndc.packSize)}
                          </TabPanel>
                        ))}
                      </TabView>
                    </div>
                  </AccordionTab>
                </Accordion>
              )}
            </div>
          </div>
        </div>

        <NotesSection
          mode="view"
          isNotesDataLoading={false}
          notesData={mockNotesData}
        />
      </div>
    </div>
  );
};

export default ConstrainedForecastView;
